import { SourceOption } from '@/components/export/configs/asset.export.config';
import type { AgoraCapability } from '@/hooks/api/useGetCapabilities';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

/**
 * Builds source options for asset export dropdown from statistics data
 *
 * Groups sources into hierarchical structure:
 * - Chariot group: Contains native capabilities and non-integration sources
 * - Integration sources: Third-party integrations as top-level items
 *
 * **IMPORTANT**: This function now uses backend capabilities data to ensure
 * consistency between filter dropdowns and table columns. Previously, it used
 * hardcoded AllIntegrations.name which caused discrepancies (e.g., filter showing
 * "Burp Enterprise" but column showing "Burp").
 *
 * @param sourceStatistics - Array of source statistics from backend API
 * @param capabilities - Array of capabilities from backend (integrations + chariot native)
 * @returns Hierarchical source options with Chariot group and integration items
 *
 * @example
 * ```tsx
 * const { data: capabilitiesData } = useGetCapabilities();
 * const sourceOptions = useMemo(
 *   () => buildSourceOptions(sourceStatistics, capabilitiesData.allCapabilities),
 *   [sourceStatistics, capabilitiesData.allCapabilities]
 * );
 * ```
 */
export function buildSourceOptions(
  sourceStatistics: Array<{ value: string }> | undefined,
  capabilities: AgoraCapability[] | undefined
): SourceOption[] {
  if (!sourceStatistics) return [];

  // Build lookup map for fast integration checking
  const integrationLookup = new Map<string, AgoraCapability>();
  if (capabilities) {
    capabilities
      .filter(cap => cap.integration === true)
      .forEach(cap => {
        integrationLookup.set(cap.name.toLowerCase(), cap);
      });
  }

  // Group non-integration sources under Chariot
  const chariotSources = sourceStatistics
    .filter(statistic => {
      // Check if this source is NOT an integration
      return !integrationLookup.has(statistic.value.toLowerCase());
    })
    .map(statistic => ({
      label: normalizeOriginName(statistic.value),
      value: statistic.value.toLowerCase(),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Integration sources as top-level items
  // Use backend title for display (consistent with vulnerability filters)
  const integrationSources = sourceStatistics
    .filter(statistic => {
      // Check if this source IS an integration
      return integrationLookup.has(statistic.value.toLowerCase());
    })
    .map(statistic => {
      const capability = integrationLookup.get(statistic.value.toLowerCase());

      return {
        // Use backend title as primary, fallback to normalized name
        label: capability?.title || normalizeOriginName(statistic.value),
        value: statistic.value,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  return [
    {
      label: 'Chariot',
      value: 'chariot',
      isGroup: true,
      items: chariotSources,
    },
    ...integrationSources,
  ];
}
